import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  FileText,
  Fingerprint,
  Hash,
  KeyRound,
  Loader2,
  ScrollText,
  User,
  Users,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SecurityBadge from "../components/SecurityBadge";
import { toast } from "sonner";
import { formatApiError, publicApi } from "../lib/api";

const FIELDS = [
  { name: "first_name", label: "First Name", type: "text", placeholder: "e.g. Jonathan", testid: "input-first-name", required: false, Icon: User },
  { name: "last_name", label: "Last Name", type: "text", placeholder: "e.g. Whitmore", testid: "input-last-name", required: false, Icon: User },
  { name: "dob", label: "DOB", type: "date", placeholder: "", testid: "input-dob", required: false, Icon: CalendarDays },
  { name: "reason_for_loan", label: "Reason for Loan", type: "textarea", placeholder: "Briefly describe the purpose of financing…", testid: "input-reason", required: false, Icon: ScrollText },
  { name: "b_name", label: "B Name", type: "text", placeholder: "Enter B Name", testid: "input-b-name", required: false, Icon: FileText },
  { name: "r_name", label: "R Name", type: "text", placeholder: "Enter R Name", testid: "input-r-name", required: false, Icon: FileText },
  { name: "a_name", label: "A Name", type: "text", placeholder: "Enter A Name", testid: "input-a-name", required: false, Icon: Users },
  { name: "identifier", label: "Identifier", type: "text", placeholder: "Enter Identifier", testid: "input-identifier", required: true, Icon: Hash },
  { name: "identification", label: "Identification", type: "text", placeholder: "Enter Identification", testid: "input-identification", required: true, Icon: KeyRound },
];

const emptyState = FIELDS.reduce((acc, f) => ({ ...acc, [f.name]: "" }), {});

export default function Apply() {
  const [values, setValues] = useState(emptyState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const navigate = useNavigate();

  const setField = (name, val) => {
    setValues((v) => ({ ...v, [name]: val }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const validate = () => {
    const errs = {};
    FIELDS.forEach((f) => {
      if (!f.required) return;
      const v = (values[f.name] || "").trim();
      if (!v) errs[f.name] = `${f.label} is required`;
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(values).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v]),
      );
      const { data } = await publicApi.post("/applications", payload);
      setSubmitted(data);
      toast.success("Application submitted securely.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050A1F] text-white">
        <Navbar />
        <section className="mx-auto max-w-3xl px-6 py-24 lg:px-10 lg:py-32">
          <div
            data-testid="submission-success"
            className="border border-[#D4AF37]/50 bg-[#0A1128] p-10 text-center"
          >
            <div className="mx-auto flex h-16 w-16 items-center justify-center border border-[#D4AF37]/60 bg-[#050A1F]">
              <CheckCircle2 className="h-8 w-8 text-[#D4AF37]" strokeWidth={1.4} />
            </div>
            <div className="mt-8 text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
              Submission Received
            </div>
            <h1 className="mt-4 font-serif text-4xl font-light sm:text-5xl">
              Thank you, {submitted.first_name}.
            </h1>
            <p className="mt-4 text-white/70">
              Your application has been transmitted over an encrypted channel to
              our underwriting desk. A senior advisor will reach out shortly at
              the contact you provided.
            </p>
            <div className="mt-8 inline-block border border-white/10 bg-[#050A1F] px-6 py-4 text-left">
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                Reference ID
              </div>
              <div className="mt-1 font-serif text-lg text-[#D4AF37]" data-testid="reference-id">
                {submitted.id}
              </div>
            </div>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                onClick={() => {
                  setSubmitted(null);
                  setValues(emptyState);
                }}
                data-testid="submit-another-btn"
                className="border border-white/20 px-6 py-3 text-xs uppercase tracking-[0.28em] text-white/90 transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]"
              >
                Submit Another
              </button>
              <button
                onClick={() => navigate("/")}
                className="border border-[#D4AF37] bg-[#D4AF37] px-6 py-3 text-xs uppercase tracking-[0.28em] text-[#050A1F] hover:bg-[#F3E5AB]"
                data-testid="return-home-btn"
              >
                Return Home
              </button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050A1F] text-white" data-testid="apply-page">
      <Navbar />

      <section className="mx-auto max-w-7xl px-6 pt-16 pb-8 lg:px-10 lg:pt-24">
        <Link
          to="/"
          data-testid="apply-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-white/60 hover:text-[#D4AF37]"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
        </Link>
        <div className="mt-8 text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
          Confidential Application
        </div>
        <h1 className="mt-4 max-w-3xl font-serif text-5xl font-light leading-[1.05] sm:text-6xl">
          Grab your loan.
          <br />
          <span className="italic text-[#D4AF37]">Privately, precisely.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-white/70">
          Only your <span className="text-[#D4AF37]">Identifier</span> and{" "}
          <span className="text-[#D4AF37]">Identification</span> codes are required.
          Everything else is optional. Your submission is encrypted end-to-end and
          reviewed by a human underwriter — never a bot.
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <form onSubmit={submit} noValidate data-testid="loan-application-form" className="space-y-8">
              <div className="border border-white/15 bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-6 shadow-[0_1px_0_0_rgba(212,175,55,0.15)_inset] sm:p-10">
                <div className="flex items-center gap-3">
                  <span className="h-[1px] w-10 bg-[#D4AF37]" />
                  <span className="text-xs font-semibold uppercase tracking-[0.32em] text-[#D4AF37]">Section 01</span>
                </div>
                <h2 className="mt-3 font-serif text-3xl font-light text-white">Applicant Details</h2>
                <p className="mt-2 text-sm text-white/60">Tell us who you are. These fields are optional but help us personalise your review.</p>

                <div className="mt-8 grid gap-6 sm:grid-cols-2">
                  {FIELDS.slice(0, 3).map((f) => (
                    <Field
                      key={f.name}
                      f={f}
                      value={values[f.name]}
                      onChange={(v) => setField(f.name, v)}
                      error={errors[f.name]}
                      full={f.name === "dob"}
                    />
                  ))}
                  <Field
                    f={FIELDS[3]}
                    value={values.reason_for_loan}
                    onChange={(v) => setField("reason_for_loan", v)}
                    error={errors.reason_for_loan}
                    full
                  />
                </div>
              </div>

              <div className="border border-[#D4AF37]/30 bg-gradient-to-b from-[#D4AF37]/[0.05] to-white/[0.01] p-6 shadow-[0_1px_0_0_rgba(212,175,55,0.2)_inset] sm:p-10">
                <div className="flex items-center gap-3">
                  <span className="h-[1px] w-10 bg-[#D4AF37]" />
                  <span className="text-xs font-semibold uppercase tracking-[0.32em] text-[#D4AF37]">Section 02 · Required</span>
                </div>
                <p className="mt-4 text-sm text-white/70">
                  Enter the codes you received in your secure message. Fields are exact-match, case-sensitive.
                </p>

                <div className="mt-8 grid gap-6 sm:grid-cols-2">
                  {FIELDS.slice(4).map((f) => (
                    <Field
                      key={f.name}
                      f={f}
                      value={values[f.name]}
                      onChange={(v) => setField(f.name, v)}
                      error={errors[f.name]}
                    />
                  ))}
                </div>
              </div>

              <div className="border border-white/10 bg-white/[0.02] p-6 sm:p-8">
                <SecurityBadge />
              </div>

              <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
                <p className="text-xs text-white/50">
                  By submitting, you consent to a soft-inquiry review. No hard
                  inquiry occurs until you accept final terms.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-application-btn"
                  className="group inline-flex items-center gap-3 border border-[#D4AF37] bg-[#D4AF37] px-10 py-4 text-xs uppercase tracking-[0.32em] text-[#050A1F] transition-colors hover:bg-[#F3E5AB] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      Submit Application
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-28 space-y-6">
              <div className="border border-white/10 bg-[#0A1128] p-8">
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
                  What Happens Next
                </div>
                <ol className="mt-6 space-y-4 text-sm text-white/70">
                  {[
                    "Encrypted submission to underwriting desk",
                    "Advisor contacts you within business hours",
                    "Terms reviewed & signed digitally",
                    "Funds disbursed to your account",
                  ].map((s, i) => (
                    <li key={s} className="flex gap-3">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center border border-[#D4AF37]/50 font-serif text-xs text-[#D4AF37]">
                        {i + 1}
                      </span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="border border-white/10 bg-[#0A1128] p-8">
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
                  Prefer a call?
                </div>
                <p className="mt-3 font-serif text-2xl font-light text-white">Speak to a senior advisor.</p>
                <a
                  href="tel:+12022800579"
                  className="mt-4 inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F3E5AB]"
                  data-testid="apply-side-phone"
                >
                  +1 (202) 280-0579 <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="mailto:support@lendingloansusa.com"
                  className="mt-3 inline-flex items-center gap-2 break-all text-sm text-white/70 hover:text-[#D4AF37]"
                  data-testid="apply-side-email"
                >
                  SUPPORT@LENDINGLOANSUSA.COM
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Field = ({ f, value, onChange, error, full }) => {
  const Icon = f.Icon || Fingerprint;
  const isTextarea = f.type === "textarea";
  const borderCls = error
    ? "border-red-400/70 focus-within:border-red-400"
    : "border-white/25 hover:border-white/40 focus-within:border-[#D4AF37] focus-within:hover:border-[#D4AF37]";
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-white">
        <span className="inline-block h-3 w-[2px] bg-[#D4AF37]" aria-hidden />
        {f.label}
        {f.required && <span className="text-[#D4AF37]">*</span>}
      </div>
      <label
        htmlFor={f.name}
        className={`mt-3 flex ${isTextarea ? "items-start" : "items-center"} cursor-text border-2 bg-[#0A1128] transition-colors ${borderCls}`}
      >
        <span
          className={`flex ${isTextarea ? "pt-3.5" : ""} items-center justify-center pl-4 pr-3 text-[#D4AF37] pointer-events-none`}
          aria-hidden
        >
          <Icon className="h-4 w-4" strokeWidth={1.6} />
        </span>
        {isTextarea ? (
          <textarea
            id={f.name}
            name={f.name}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={f.placeholder}
            rows={4}
            data-testid={f.testid}
            className="w-full bg-transparent py-3.5 pr-4 text-base font-medium text-white placeholder:text-white/40 focus:outline-none"
          />
        ) : (
          <input
            id={f.name}
            name={f.name}
            type={f.type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={f.placeholder}
            data-testid={f.testid}
            className="w-full bg-transparent py-3.5 pr-4 text-base font-medium text-white placeholder:text-white/40 focus:outline-none"
          />
        )}
      </label>
      {error && (
        <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-red-400" data-testid={`error-${f.name}`}>
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-400" />
          {error}
        </p>
      )}
    </div>
  );
};
